import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';
import {
  LayoutDashboard,
  Hexagon,
  MessageSquare,
  Target,
  Users,
  Settings,
  ShieldAlert,
  LogOut,
  Menu,
  X,
  Brain,
  FileText,
  ClipboardList,
  BarChart3,
  ListChecks,
  TrendingUp,
  Activity,
  Trophy,
  Cpu,
  Award,
  Crown,
  CalendarPlus,
  ChevronDown,
} from 'lucide-react';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { getBackendBaseUrl } from '../config/apiBaseUrl';

const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const backendBaseUrl = getBackendBaseUrl();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const baseNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/chat', icon: MessageSquare, label: 'Conversar com o ELIOS' },
  ];

  const userOnlyNavItems = [
    { path: '/pilares', icon: Hexagon, label: 'Pilares' },
    { path: '/metas', icon: Target, label: 'Metas' },
    { path: '/dashboard/weekly-progress', icon: TrendingUp, label: 'Progresso Semanal' },
    baseNavItems[1],
    { path: '/historico-nps', icon: ListChecks, label: 'Histórico de NPS' },
    { path: '/ranking', icon: Crown, label: 'Ranking de Pontos' },
  ];

  const navItems = isAdmin() ? baseNavItems : [baseNavItems[0], ...userOnlyNavItems];

  const adminNavGroups = [
    {
      id: 'membros',
      label: 'Pessoas',
      icon: Users,
      children: [
        { path: '/admin/usuarios', icon: Users, label: 'Usuários' },
        { path: '/admin/mentorados', icon: ClipboardList, label: 'Respostas dos Mentorados' },
      ],
    },
    {
      id: 'engajamento',
      label: 'Gamificação',
      icon: BarChart3,
      children: [
        { path: '/admin/gamificacao', icon: Award, label: 'Painel de Gamificação' },
        { path: '/admin/game-ranking', icon: Crown, label: 'Ranking de Gamificação' },
        { path: '/admin/game-audit', icon: ShieldAlert, label: 'Auditoria de Pontos' },
        { path: '/admin/eventos', icon: CalendarPlus, label: 'Pontos de Eventos' },
      ],
    },
    {
      id: 'progresso',
      label: 'Acompanhamento',
      icon: TrendingUp,
      children: [
        { path: '/admin/weekly-progress', icon: TrendingUp, label: 'Progresso Semanal' },
        { path: '/admin/nps', icon: BarChart3, label: 'Gestão de NPS' },
        { path: '/admin/magnus', icon: Trophy, label: 'Magnus' },
      ],
    },
    {
      id: 'conteudo-ia',
      label: 'Conteúdo e IA',
      icon: Brain,
      children: [
        { path: '/admin/perguntas', icon: FileText, label: 'Base de Perguntas' },
        { path: '/admin/elios', icon: Brain, label: 'Treinamento do ELIOS' },
      ],
    },
    {
      id: 'operacao',
      label: 'Operação',
      icon: Cpu,
      children: [
        { path: '/admin/jobs', icon: Cpu, label: 'Automações e Envios' },
        { path: '/admin/observabilidade', icon: Activity, label: 'Monitoramento' },
      ],
    },
  ];

  const adminStandaloneItems = [
    { path: '/admin/configuracoes', icon: Settings, label: 'Configurações' },
  ];

  useEffect(() => {
    const activeGroup = adminNavGroups.find((group) =>
      group.children.some((child) => location.pathname === child.path)
    );
    if (!activeGroup) return;
    setOpenGroups((prev) => (prev.has(activeGroup.id) ? prev : new Set(prev).add(activeGroup.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path;
    return (
      <NavLink
        to={item.path}
        onClick={() => setMobileOpen(false)}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
          isActive
            ? 'sidebar-active text-white'
            : 'text-neutral-400 hover:text-white hover:bg-white/5'
        }`}
        data-testid={`nav-${item.path.replace('/', '')}`}
      >
        <item.icon size={20} strokeWidth={1.5} />
        <span className="font-medium">{item.label}</span>
      </NavLink>
    );
  };

  const NavGroup = ({ group }) => {
    const isOpen = openGroups.has(group.id);
    const hasActiveChild = group.children.some((child) => location.pathname === child.path);
    return (
      <Collapsible open={isOpen} onOpenChange={() => toggleGroup(group.id)}>
        <CollapsibleTrigger
          className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
            hasActiveChild ? 'text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid={`nav-group-${group.id}`}
        >
          <span className="flex items-center gap-3">
            <group.icon size={20} strokeWidth={1.5} />
            <span className="font-medium">{group.label}</span>
          </span>
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pl-4 space-y-1 mt-1">
          {group.children.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const profilePhotoUrl = user?.profile_photo_url
    ? user.profile_photo_url.startsWith('http')
      ? user.profile_photo_url
      : `${backendBaseUrl}${user.profile_photo_url}`
    : null;

  const SidebarContent = () => (
    <div className="flex flex-col h-full min-h-0">
      {/* Logo */}
      <div className="p-6 border-b border-white/5">
        <Logo size="md" />
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-white/5">
        <div className="glass-card rounded-2xl p-3">
          <div className="flex items-center gap-3">
            {profilePhotoUrl && (
              <img
                src={profilePhotoUrl}
                alt={`Foto de ${user?.full_name || 'usuário'}`}
                className="h-12 w-12 rounded-full object-cover border border-white/10"
              />
            )}
            <div className="min-w-0">
              <p className="text-sm text-neutral-400">Bem-vindo,</p>
              <p className="text-white font-semibold truncate">{user?.full_name}</p>
              <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded-full ${
                isAdmin() ? 'bg-white/10 text-white' : 'bg-amber-500/20 text-amber-400'
              }`}>
                {user?.role}
              </span>
            </div>
          </div>
          <Button
            variant="ghost"
            className="mt-3 h-9 w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10"
            onClick={handleLogout}
            data-testid="logout-btn-user-card"
          >
            <LogOut size={16} className="mr-2" />
            Sair do sistema
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
        <p className="text-[10px] text-slate-500 uppercase tracking-[0.28em] px-4 mb-2">Menu</p>
        {navItems.map((item) => (
          <NavItem key={item.path} item={item} />
        ))}

        {isAdmin() && (
          <>
            <div className="my-4 border-t border-white/5" />
            <p className="text-[10px] text-slate-500 uppercase tracking-[0.28em] px-4 mb-2">Administração</p>
            {[adminNavGroups[0], adminNavGroups[2], adminNavGroups[1], ...adminNavGroups.slice(3)].map((group) => (
              <NavGroup key={group.id} group={group} />
            ))}
            {adminStandaloneItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </>
        )}
      </nav>

    </div>
  );

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="fixed top-4 left-4 z-50 p-2 rounded-full bg-secondary text-white md:hidden"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile overlay */}
      <div 
        className={`sidebar-overlay md:hidden ${mobileOpen ? 'open' : ''}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-64 bg-hutoo-950/95 border-r border-white/5 backdrop-blur-lg z-50 
          transform transition-transform duration-300 md:translate-x-0
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
